// frontend/modules/customer-category/hooks/useCustomerOnboarding.ts
import { useCallback, useState, useEffect } from 'react';
import { message } from 'antd';
import { useCustomerCategories } from './useCustomerCategories';
import { CustomerCategoryDTO, CustomerOnboardingData } from '../types';
import { UserCategoryAccessDTO } from '@/shared/api/model';
import {useGetCategoriesForUserGroup} from "@/shared/api/client/customer-category-controller";

export const useCustomerOnboarding = () => {
    const getErrorMessage = (error: unknown, fallback: string) => {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        if (typeof error === 'object' && error !== null && 'message' in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }

        return fallback;
    };

    // Get customer categories functionality
    const {
        fetchCategoryById,
        activeCategory: selectedCategory
    } = useCustomerCategories();

    // State for user category access
    const [userCategoryAccess, setUserCategoryAccess] = useState<UserCategoryAccessDTO | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Use orval-generated hook to fetch categories for user group
    const {
        data: userCategoryAccessData,
        isLoading: isFetchingCategories,
        error: fetchError,
        refetch: refetchUserCategoryAccess
    } = useGetCategoriesForUserGroup();

    // Update local state when data changes
    useEffect(() => {
        if (userCategoryAccessData) {
            // @ts-ignore
            setUserCategoryAccess(userCategoryAccessData);
            setError(null);
        }
    }, [userCategoryAccessData]);

    // Update loading state
    useEffect(() => {
        setIsLoading(isFetchingCategories);
    }, [isFetchingCategories]);

    // Handle fetch errors
    useEffect(() => {
        if (fetchError) {
            console.error('Error fetching user category access:', fetchError);
            setError(getErrorMessage(fetchError, 'Failed to fetch user category access'));
            message.error('Failed to fetch available categories');
        }
    }, [fetchError]);

    // Fetch categories available for the current user
    const fetchUserCategoryAccess = useCallback(async () => {
        try {
            await refetchUserCategoryAccess();
        } catch (error: unknown) {
            console.error('Error refetching user category access:', error);
            setError(getErrorMessage(error, 'Failed to fetch user category access'));
            message.error('Failed to fetch available categories');
        }
    }, [refetchUserCategoryAccess]);

    // Create a new customer
    const createCustomer = useCallback(async (formData: CustomerOnboardingData) => {
        try {
            setIsLoading(true);

            // Prepare the customer data
            const customerData = {
                ...formData.customerData,
                categoryId: formData.categoryId,
                products: formData.selectedProducts.map(p => ({
                    glNo: p.glNo,
                    productType: p.product
                })),
                custInfoData: formData.custInfoData,
                isFreestyle: formData.isFreestyle
            };

            // TODO: Implement customer creation using orval-generated client
            // This would typically use a hook like useCreateCustomer from the orval-generated client
            
            // For now, simulate success
            message.success('Customer created successfully (simulated)');
            return customerData;
            
        } catch (error: unknown) {
            console.error('Error creating customer:', error);
            setError(getErrorMessage(error, 'Failed to create customer'));
            message.error('Failed to create customer');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        userCategoryAccess,
        selectedCategory,
        isLoading,
        error,

        fetchUserCategoryAccess,
        fetchCategoryById,
        createCustomer
    };
};